/**
 * LabelChip - Component for displaying a single label as a Chip
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { Label } from '../../types/labels';

interface LabelChipProps {
  label: Label;
  size?: 'small' | 'medium';
  onDelete?: () => void;
  onClick?: () => void;
}

const LabelChip: React.FC<LabelChipProps> = ({
  label,
  size = 'small',
  onDelete,
  onClick,
}) => {
  const chipStyle = {
    backgroundColor: label.color || '#1976d2',
    color: getContrastColor(label.color || '#1976d2'),
    cursor: onClick ? 'pointer' : 'default',
    '&:hover': onClick ? { opacity: 0.8 } : {},
  };

  return (
    <Tooltip title={label.description || label.name} arrow>
      <Chip
        label={label.name}
        size={size}
        sx={chipStyle}
        onDelete={onDelete}
        onClick={onClick}
        // Icon support can be added later with dynamic icon loading from Material-UI icons
      />
    </Tooltip>
  );
};

/**
 * Get contrasting text color (black or white) based on background color
 */
function getContrastColor(hexColor: string): string {
  if (!hexColor || !hexColor.startsWith('#')) {
    return '#ffffff';
  }

  // Remove # and convert to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light colors, white for dark colors
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export default LabelChip;
